/**
 * TeamDetailPage Component
 * Displays detailed information about a specific team including members and role distribution
 */

import { useState, useEffect, useMemo } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { TeamFormModal } from '../components/teams/TeamFormModal';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Breadcrumbs, getRootBreadcrumb, type BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { teamsService } from '../services/teams.service';
import { departmentsService } from '../services/departments.service';
import { membersService } from '../services/members.service';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getInitials } from '../lib/utils';

interface RoleSummary {
  member_id: number;
  first_name: string;
  last_name: string;
  email?: string;
  has_profile_picture?: boolean;
}

export const TeamDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const teamId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const backLabel = (location.state as { backLabel?: string } | null)?.backLabel;
  const queryClient = useQueryClient();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [teamLogoUrl, setTeamLogoUrl] = useState<string | null>(null);
  const [memberImageUrls, setMemberImageUrls] = useState<Map<number, string>>(new Map());

  // Scroll to top when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Fetch the team detail (includes managers + leads arrays)
  const { data: team, isLoading: isLoadingTeam, dataUpdatedAt } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamsService.getById(teamId),
    enabled: Number.isFinite(teamId),
  });

  // Fetch all members (used for sorting team members and the manager/lead picker)
  const { data: allMembers = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
  });

  // Fetch parent department detail (for the read-only Directors / Dept Managers right column).
  // Skipped when the team has no department.
  const { data: parentDept } = useQuery({
    queryKey: ['department', team?.department_id],
    queryFn: () => departmentsService.getById(team!.department_id!),
    enabled: !!team?.department_id,
  });

  // Lookup map: id → Member, used to enrich manager/lead summaries with tenure + photos.
  const memberById = useMemo(() => {
    const map = new Map<number, typeof allMembers[number]>();
    allMembers.forEach((m) => map.set(m.id, m));
    return map;
  }, [allMembers]);

  const managerIds = useMemo(
    () => new Set((team?.managers ?? []).map((m) => m.member_id)),
    [team?.managers]
  );
  const leadIds = useMemo(
    () => new Set((team?.leads ?? []).map((l) => l.member_id)),
    [team?.leads]
  );

  // Members rendered in the left list are the union of:
  //   1. Members whose team_id === this team (regular team members)
  //   2. This team's managers (any tenant member, regardless of team_id)
  //   3. This team's leads (any tenant member, regardless of team_id)
  // De-duped by member id; sorted managers → leads → others, tenure asc within each group.
  const teamMembers = useMemo(() => {
    if (!team) return [];
    const inSet = new Set<number>();
    const result: typeof allMembers = [];
    const push = (m: typeof allMembers[number] | undefined) => {
      if (!m || m.removed_at || inSet.has(m.id)) return;
      inSet.add(m.id);
      result.push(m);
    };
    allMembers.forEach((m) => {
      if (m.team_id === team.id) push(m);
    });
    (team.managers ?? []).forEach((mgr) => push(memberById.get(mgr.member_id)));
    (team.leads    ?? []).forEach((lead) => push(memberById.get(lead.member_id)));

    const tenure = (m: { id: number }) => {
      const full = memberById.get(m.id);
      return full ? new Date(full.date_joined || 0).getTime() : 0;
    };
    const groupOf = (m: { id: number }) =>
      managerIds.has(m.id) ? 0 : leadIds.has(m.id) ? 1 : 2;
    return result.sort((a, b) => {
      const ga = groupOf(a);
      const gb = groupOf(b);
      if (ga !== gb) return ga - gb;
      return tenure(a) - tenure(b);
    });
  }, [allMembers, team, managerIds, leadIds, memberById]);

  // Build breadcrumb hierarchy
  const breadcrumbItems = useMemo(() => {
    if (!team) return [];
    const items: BreadcrumbItem[] = [getRootBreadcrumb()];
    if (team.department_id && team.department_name) {
      items.push({ label: team.department_name, to: `/departments/${team.department_id}` });
    }
    return items;
  }, [team]);

  // Calculate job title distribution (members.position)
  const roleDistribution = useMemo(() => {
    const distribution = new Map<string, number>();
    teamMembers.forEach((member) => {
      const position = member.position || 'No job title';
      distribution.set(position, (distribution.get(position) || 0) + 1);
    });
    return Array.from(distribution.entries())
      .map(([position, count]) => ({ position, count }))
      .sort((a, b) => b.count - a.count);
  }, [teamMembers]);

  // Pick the highest-ranked person for the header summary line.
  // Manager beats lead; within a tier, the API already sorts by tenure so [0] wins.
  const headerHighlight = useMemo(() => {
    const managers = team?.managers ?? [];
    const leads = team?.leads ?? [];
    const total = managers.length + leads.length;
    if (managers.length > 0) {
      const m = managers[0];
      return {
        label: 'Team Manager',
        memberId: m.member_id,
        firstName: m.first_name,
        lastName: m.last_name,
        extra: total - 1,
      };
    }
    if (leads.length > 0) {
      const l = leads[0];
      return {
        label: 'Lead',
        memberId: l.member_id,
        firstName: l.first_name,
        lastName: l.last_name,
        extra: total - 1,
      };
    }
    return null;
  }, [team?.managers, team?.leads]);

  // Load team logo
  useEffect(() => {
    if (!team?.team_logo) {
      setTeamLogoUrl(null);
      return;
    }
    let cancelled = false;
    setTeamLogoUrl(null);
    teamsService.fetchTeamImage(team.id).then((url) => {
      if (!cancelled && url) {
        setTeamLogoUrl(url);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [team?.id, team?.team_logo, dataUpdatedAt]);

  // Load member profile images for the team-members list
  useEffect(() => {
    let cancelled = false;
    const newImageUrls = new Map<number, string>();

    const loadImages = async () => {
      for (const member of teamMembers) {
        if (member.has_profile_picture) {
          try {
            const url = await membersService.fetchMemberImage(member.id);
            if (url && !cancelled) {
              newImageUrls.set(member.id, url);
            }
          } catch (error) {
            console.error(`Failed to load image for member ${member.id}`, error);
          }
        }
      }
      if (!cancelled) {
        setMemberImageUrls(newImageUrls);
      }
    };

    loadImages();
    return () => {
      cancelled = true;
    };
  }, [teamMembers]);

  // Mutations: add/remove team manager + lead
  const addManagerMutation = useMutation({
    mutationFn: (memberId: number) => teamsService.addManager(teamId, memberId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
    onError: (error: any) => toast.error(error.response?.data?.error || 'Failed to add manager'),
  });
  const removeManagerMutation = useMutation({
    mutationFn: (memberId: number) => teamsService.removeManager(teamId, memberId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
    onError: (error: any) => toast.error(error.response?.data?.error || 'Failed to remove manager'),
  });
  const addLeadMutation = useMutation({
    mutationFn: (memberId: number) => teamsService.addLead(teamId, memberId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
    onError: (error: any) => toast.error(error.response?.data?.error || 'Failed to add lead'),
  });
  const removeLeadMutation = useMutation({
    mutationFn: (memberId: number) => teamsService.removeLead(teamId, memberId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team', teamId] }),
    onError: (error: any) => toast.error(error.response?.data?.error || 'Failed to remove lead'),
  });

  // Role-swap mutation: removes a member from one role then adds to the other.
  // Triggered when admin picks a member already holding the cross role on this team.
  const swapMutation = useMutation({
    mutationFn: async (params: { memberId: number; from: 'manager' | 'lead'; to: 'manager' | 'lead' }) => {
      if (params.from === 'manager') {
        await teamsService.removeManager(teamId, params.memberId);
      } else {
        await teamsService.removeLead(teamId, params.memberId);
      }
      if (params.to === 'manager') {
        await teamsService.addManager(teamId, params.memberId);
      } else {
        await teamsService.addLead(teamId, params.memberId);
      }
    },
    onSuccess: () => {
      toast.success('Role updated');
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
    onError: (error: any) => toast.error(error.response?.data?.error || 'Failed to change role'),
  });

  // When admin picks a member already holding the cross role, prompt for confirmation
  // before swapping. Same UX as DepartmentDetailPage.
  const [pendingSwap, setPendingSwap] = useState<{
    memberId: number;
    memberName: string;
    from: 'manager' | 'lead';
    to: 'manager' | 'lead';
  } | null>(null);

  const handleAddTeamManager = (memberId: number) => {
    if (leadIds.has(memberId)) {
      const m = allMembers.find((x) => x.id === memberId);
      if (m) {
        setPendingSwap({
          memberId,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'lead',
          to: 'manager',
        });
      }
      return;
    }
    addManagerMutation.mutate(memberId);
  };

  const handleAddTeamLead = (memberId: number) => {
    if (managerIds.has(memberId)) {
      const m = allMembers.find((x) => x.id === memberId);
      if (m) {
        setPendingSwap({
          memberId,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'manager',
          to: 'lead',
        });
      }
      return;
    }
    addLeadMutation.mutate(memberId);
  };

  const deleteTeamMutation = useMutation({
    mutationFn: (id: number) => teamsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Team deleted successfully');
      navigate('/teams');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete team');
    },
  });

  const handleDeleteTeam = () => {
    if (team) {
      deleteTeamMutation.mutate(team.id);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Unknown';
    }
  };

  if (isLoadingTeam || isLoadingMembers) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!team) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-heading mb-2">Team Not Found</h2>
          <p className="text-meta mb-4">The team you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/teams')}>Back to Teams</Button>
        </div>
      </Layout>
    );
  }

  const isAdmin = hasAdminAccess(user?.role);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center justify-between">
          <Breadcrumbs items={breadcrumbItems} />
          <Button variant="secondary" className="hidden lg:inline-flex" onClick={() => backLabel ? navigate(-1) : navigate('/teams')}>
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to {backLabel || 'Teams'}
          </Button>
        </div>

        {/* Team Header */}
        <div className="bg-surface rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            {/* Left: Team Info */}
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {teamLogoUrl ? (
                  <img
                    src={teamLogoUrl}
                    alt={team.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-2xl font-bold">
                    {getInitials(team.name, '')}
                  </div>
                )}
              </div>

              <div>
                <h1 className="text-2xl font-bold text-heading">{team.name}</h1>
                <div className="flex items-center space-x-4 mt-2 text-sm text-meta">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {headerHighlight ? (
                      <>
                        {headerHighlight.label}:{'\u00A0'}
                        <Link
                          to={`/members/${headerHighlight.memberId}`}
                          state={{ backLabel: 'Team' }}
                          className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          {headerHighlight.firstName} {headerHighlight.lastName}
                        </Link>
                        {headerHighlight.extra > 0 && (
                          <>{' '}<span>+{headerHighlight.extra}</span></>
                        )}
                      </>
                    ) : (
                      'No lead assigned'
                    )}
                  </span>
                  <span className="hidden md:flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}
                  </span>
                  <span className="hidden lg:flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    Created {formatDate(team.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Action Buttons (Admin Only) — visible at md+; mobile copy lives below the card */}
            {isAdmin && (
              <div className="hidden md:flex items-center space-x-9">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Team
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Team
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content: Team Members + Right Column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Team Members List */}
          <div className="lg:col-span-2">
            <div className="bg-surface rounded-lg shadow">
              <div className="p-6 border-b border-[var(--border-default)]">
                <h2 className="text-xl font-semibold text-heading flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  Team Members ({teamMembers.length})
                </h2>
              </div>

              {teamMembers.length === 0 ? (
                <div className="p-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-meta" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="mt-2 text-sm text-meta">No members in this team yet</p>
                </div>
              ) : (
                <div className="divide-y divide-[var(--border-default)]">
                  {teamMembers.map((member) => {
                    const isManager = managerIds.has(member.id);
                    const isLead = leadIds.has(member.id);
                    return (
                      <div
                        key={member.id}
                        className="relative p-4 hover:bg-surface-hover active:bg-surface-hover transition-colors"
                      >
                        <Link
                          to={`/members/${member.id}`}
                          state={{ backLabel: 'Team' }}
                          className="absolute inset-0 z-10 can-hover:hidden"
                          tabIndex={-1}
                          aria-label={`View ${member.first_name} ${member.last_name}`}
                        />
                        <div className="flex items-center space-x-4">
                          <Link
                            to={`/members/${member.id}`}
                            state={{ backLabel: 'Team' }}
                            className="flex-shrink-0 relative z-20"
                          >
                            {memberImageUrls.get(member.id) ? (
                              <img
                                src={memberImageUrls.get(member.id)}
                                alt={`${member.first_name} ${member.last_name}`}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-sm font-medium">
                                {getInitials(member.first_name, member.last_name)}
                              </div>
                            )}
                          </Link>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                              <Link
                                to={`/members/${member.id}`}
                                state={{ backLabel: 'Team' }}
                                className="relative z-20 text-base font-medium text-heading hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              >
                                {member.first_name} {member.last_name}
                              </Link>
                              {isManager && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-status-warning text-status-warning">
                                  Team Manager
                                </span>
                              )}
                              {isLead && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-status-info text-status-info">
                                  Team Lead
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-meta">
                              <div>{member.position || 'No job title'}</div>
                              <div className="text-meta">{member.email}</div>
                            </div>
                          </div>

                          {/* Tap affordance — touch devices only */}
                          <div className="hidden touch:flex items-center text-meta flex-shrink-0" aria-hidden="true">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 6l6 6-6 6" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Directors (read-only, from parent department) */}
            {parentDept && parentDept.directors && parentDept.directors.length > 0 && (
              <RoleSection
                title="Directors"
                iconColorClass="text-status-accent"
                members={parentDept.directors}
                emptyText="No directors assigned"
                readOnly
              />
            )}

            {/* Department Managers (read-only, from parent department) */}
            {parentDept && parentDept.managers && parentDept.managers.length > 0 && (
              <RoleSection
                title="Department Managers"
                iconColorClass="text-purple-600"
                members={parentDept.managers}
                emptyText="No managers assigned"
                readOnly
              />
            )}

            {/* Team Managers — editable for admins. All active non-managers are candidates;
                cross-role members (existing leads) trigger a swap-confirmation modal. */}
            <RoleSection
              title="Team Managers"
              iconColorClass="text-status-warning"
              members={team.managers ?? []}
              emptyText="No team managers assigned."
              readOnly={!isAdmin}
              addLabel="Add team manager"
              onAdd={handleAddTeamManager}
              onRemove={(memberId) => removeManagerMutation.mutate(memberId)}
              candidateMembers={allMembers.filter(
                (m) => !m.removed_at && !managerIds.has(m.id)
              )}
            />

            {/* Team Leads — editable for admins. Cross-role members (existing managers)
                trigger a swap-confirmation modal. */}
            <RoleSection
              title="Team Leads"
              iconColorClass="text-status-info"
              members={team.leads ?? []}
              emptyText="No team leads assigned."
              readOnly={!isAdmin}
              addLabel="Add team lead"
              onAdd={handleAddTeamLead}
              onRemove={(memberId) => removeLeadMutation.mutate(memberId)}
              candidateMembers={allMembers.filter(
                (m) => !m.removed_at && !leadIds.has(m.id)
              )}
            />

            {/* Job Title Distribution */}
            <div className="bg-surface rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-heading mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                Job Title Distribution
              </h2>

              {roleDistribution.length === 0 ? (
                <p className="text-sm text-meta">No roles to display</p>
              ) : (
                <div className="space-y-3">
                  {roleDistribution.map(({ position, count }) => (
                    <div key={position} className="flex items-center justify-between">
                      <span className="text-sm text-body">{position}</span>
                      <span className="text-sm font-semibold text-heading">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile-only Action Buttons (below 768px) — at the bottom of the page on the darker background */}
        {isAdmin && (
          <div className="md:hidden flex items-center space-x-9">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsEditModalOpen(true)}
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Team
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Team
            </Button>
          </div>
        )}
      </div>

      {/* Edit Team Modal */}
      <TeamFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        teamId={team.id}
      />

      {/* Role-swap Confirmation Modal */}
      {pendingSwap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-elevated rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-heading mb-2">Change role</h3>
            <p className="text-sm text-meta mb-6">
              <strong>{pendingSwap.memberName}</strong> is currently a <strong>team {pendingSwap.from}</strong>{' '}
              of this team. Assigning them as <strong>team {pendingSwap.to}</strong> will remove the team{' '}
              {pendingSwap.from} role.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setPendingSwap(null)}
                disabled={swapMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const params = pendingSwap;
                  setPendingSwap(null);
                  swapMutation.mutate(params);
                }}
                isLoading={swapMutation.isPending}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-elevated rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-heading mb-2">Delete Team</h3>
            <p className="text-sm text-meta mb-6">
              Are you sure you want to delete "{team.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteTeam}
                isLoading={deleteTeamMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

/* ─── Right-column role section (Directors / Dept Managers / Team Managers / Team Leads) ─── */

interface RoleSectionProps {
  title: string;
  iconColorClass: string;
  members: RoleSummary[];
  emptyText: string;
  readOnly?: boolean;
  addLabel?: string;
  onAdd?: (memberId: number) => void;
  onRemove?: (memberId: number) => void;
  candidateMembers?: Array<{ id: number; first_name: string; last_name: string; removed_at: string | null }>;
}

const RoleSection = ({
  title,
  iconColorClass,
  members,
  emptyText,
  readOnly = false,
  addLabel,
  onAdd,
  onRemove,
  candidateMembers = [],
}: RoleSectionProps) => {
  const [imageUrls, setImageUrls] = useState<Map<number, string>>(new Map());

  // Lazy-load profile pictures for members shown in this section.
  // Cancellation guard handles the unmount-during-fetch race.
  useEffect(() => {
    let cancelled = false;
    const newUrls = new Map<number, string>();

    const loadImages = async () => {
      for (const m of members) {
        if (m.has_profile_picture) {
          try {
            const url = await membersService.fetchMemberImage(m.member_id);
            if (url && !cancelled) {
              newUrls.set(m.member_id, url);
            }
          } catch (err) {
            console.error(`Failed to load image for member ${m.member_id}`, err);
          }
        }
      }
      if (!cancelled) {
        setImageUrls(newUrls);
      }
    };

    if (members.length > 0) loadImages();
    return () => {
      cancelled = true;
    };
  }, [members]);

  return (
    <div className="bg-surface rounded-lg shadow">
      <div className="p-6 border-b border-[var(--border-default)]">
        <h2 className="text-xl font-semibold text-heading flex items-center">
          <svg className={`w-5 h-5 mr-2 ${iconColorClass}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          {title} ({members.length})
        </h2>
      </div>

      {members.length > 0 ? (
        <div className="divide-y divide-[var(--border-default)]">
          {members.map((m) => (
            <div
              key={m.member_id}
              className="p-4 flex items-center justify-between hover:bg-surface-hover transition-colors"
            >
              <div className="flex items-center space-x-3 min-w-0">
                <Link
                  to={`/members/${m.member_id}`}
                  state={{ backLabel: title }}
                  className="flex-shrink-0"
                >
                  {imageUrls.get(m.member_id) ? (
                    <img
                      src={imageUrls.get(m.member_id)}
                      alt={`${m.first_name} ${m.last_name}`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-sm font-medium">
                      {getInitials(m.first_name, m.last_name)}
                    </div>
                  )}
                </Link>
                <div className="min-w-0">
                  <Link
                    to={`/members/${m.member_id}`}
                    state={{ backLabel: title }}
                    className="text-sm font-medium text-heading hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate block"
                  >
                    {m.first_name} {m.last_name}
                  </Link>
                  {m.email && <p className="text-xs text-meta truncate">{m.email}</p>}
                </div>
              </div>
              {!readOnly && onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(m.member_id)}
                  className="flex-shrink-0 ml-2 p-1 text-meta hover:text-red-500 transition-colors"
                  title={`Remove ${m.first_name} ${m.last_name}`}
                  aria-label={`Remove ${m.first_name} ${m.last_name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center">
          <p className="text-sm text-meta">{emptyText}</p>
        </div>
      )}

      {!readOnly && onAdd && (
        <div className="p-4 border-t border-[var(--border-default)]">
          <SearchableSelect
            value=""
            onChange={(value) => {
              if (value) onAdd(Number(value));
            }}
            options={[
              { value: '', label: addLabel ?? 'Add member' },
              ...candidateMembers.map((member) => ({
                value: String(member.id),
                label: `${member.first_name} ${member.last_name}`,
              })),
            ]}
            placeholder="Search members..."
          />
        </div>
      )}
    </div>
  );
};
